import React, { useState, useMemo } from "react";
import { Row, Col, Table, Dropdown, Button, Form, Tooltip, Pagination, notification, Checkbox, Input, Space } from "antd";
import { ComponentTitle } from "../../custom-components";
import { UnmatchedAssetInterface } from '../interfaces';
import { TablePaginationConfig } from "antd/es/table/interface";
import styled from 'styled-components';
import { ReloadOutlined, SettingOutlined, SyncOutlined, PlayCircleOutlined, SearchOutlined } from '@ant-design/icons';
import AlternateTitleForm from "../modal/AlternateTitleForm";
import useAlternateTitles from "../hooks/useAlternateTitles";
import { authAxios, getEmailFromSessionStorage } from "../../utils/session_utils";

const StyledDiv = styled.div`
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent content from spilling outside container */
  position: relative; /* Required for absolute positioning of pagination */
  
  .ant-table-container {
    height: calc(100% - 100px); /* Ensure enough space for pagination */
  }
  
  .ant-table {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  .ant-table-wrapper, .ant-spin-nested-loading, .ant-spin-container {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .ant-table-row {
    background-color: white;
  }
  .ant-table-row-selected {
    background-color: #e6f7ff;
  }
  .ant-table-row:hover {
    background-color: #f5f5f5;
  }
  .ant-table-row-selected:hover {
    background-color: #e6f7ff;
  }
  .processing-row {
    opacity: 0.6;
    background-color: #f0f0f0 !important;
    pointer-events: none;
  }
  
  .ant-pagination {
    display: flex !important;
    justify-content: flex-end !important;
  }

  .ant-table-body {
    max-height: calc(-324px + 100vh) !important;
  }
`;

interface UnmatchedAssetsProps {
    height: number;
    isLoading: boolean;
    titleConflictData: UnmatchedAssetInterface[];
    fetchTitleConflicts: () => void;
    titleConflictColumns: any[];
    pagination: any;
    onConflictSelect: (record: UnmatchedAssetInterface) => void;
    selectedConflict: UnmatchedAssetInterface | null;
    linkedMetadata: { [key: string]: { ref_id: string, operation: string, language: string, title: string } };
    onRemoveRecord?: (record: UnmatchedAssetInterface) => void;
    processingRecords?: { [key: string]: boolean };
    onAlternateTitleCreated?: (record: UnmatchedAssetInterface) => void;
    onSearchMetadata?: (searchTerm: string) => void;
}

const UnmatchedAssets: React.FC<UnmatchedAssetsProps> = ({
    height,
    isLoading,
    titleConflictData,
    fetchTitleConflicts,
    titleConflictColumns,
    onConflictSelect,
    selectedConflict,
    linkedMetadata,
    onRemoveRecord,
    processingRecords = {},
    onAlternateTitleCreated,
    onSearchMetadata
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(6); // Changed default from 10 to 6
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [columnFilters, setColumnFilters] = useState<{ [key: string]: any[] }>({});
    const [sortConfig, setSortConfig] = useState<{ columnKey: string; order: 'ascend' | 'descend' } | null>(null);
    
    // Use the alternate titles hook
    const alternateTitles = useAlternateTitles();

    // Create a type-safe version of the function
    const getCheckboxProps = (record: UnmatchedAssetInterface) => {
        const result: { disabled?: boolean; name?: string } = {};
        
        if (record && record.id) {
            // Disable selection for processing or pending records
            result.disabled = processingRecords[record.id] === true || 
                              record.processed === 'pending' || 
                              record.isProcessing === true;
            result.name = record.id;
        } else {
            result.disabled = false;
        }
        
        return result;
    };

    // Remove rowSelection - we'll handle row selection differently

    const handleRemoveMarkAsPromo = (record: UnmatchedAssetInterface) => {
        if (onRemoveRecord) {
            onRemoveRecord(record);
        }
    };

    const handleOpenAlternateTitleForm = (record: UnmatchedAssetInterface) => {
        alternateTitles.openForm(record);
    };

    const handleSaveAlternateTitle = async () => {
        try {
            // Call original save method which returns the record
            const record = await alternateTitles.saveAlternateTitle();
            
            // Mark record as processing
            if (record && onAlternateTitleCreated) {
                onAlternateTitleCreated(record);
            }
        } catch (error) {
            // Error already handled in the hook, no need to do anything here
            console.error("Failed to save alternate title:", error);
        }
    };

    const handleReprocessAllConflicts = async () => {
        setIsReprocessing(true);
        try {
            const userEmail = getEmailFromSessionStorage();
            
            const config = {
                method: "POST",
                url: `${process.env.REACT_APP_BACK_END_API}/snowflake/title-conflict/reprocess-all`,
                headers: { "Content-Type": "application/json" },
                data: JSON.stringify({ 
                    userEmail: userEmail || '',
                    jobType: 'Rerun Asset Matching Process on All Conflicts'
                }),
            };

            const response = await authAxios(config);
            
            if (response.status === 200 || response.status === 202) {
                notification.success({
                    message: 'Reprocess Started',
                    description: 'Asset matching reprocess has been initiated for all conflicts.',
                });
                // Refresh the data after starting reprocess
                fetchTitleConflicts();
            } else {
                throw new Error('Unexpected response status');
            }
        } catch (error) {
            console.error("Failed to start reprocess:", error);
            notification.error({
                message: 'Reprocess Failed',
                description: 'An error occurred while starting the reprocess. Please try again.',
            });
        } finally {
            setIsReprocessing(false);
        }
    };

    // Create custom filter dropdown with Select All functionality
    const createFilterDropdown = (dataIndex: string, options: { text: string, value: string }[]) => {
        const FilterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
            const [searchValue, setSearchValue] = useState('');
            
            // Filter options based on search
            const filteredOptions = options.filter(option => 
                option.text.toLowerCase().includes(searchValue.toLowerCase()) &&
                option.value !== '__SELECT_ALL__' && option.value !== '__UNSELECT_ALL__'
            );

            const handleSelectAll = () => {
                const allValues = filteredOptions.map(option => option.value);
                setSelectedKeys(allValues);
            };

            const handleUnselectAll = () => {
                setSelectedKeys([]);
            };

            const handleConfirm = () => {
                confirm();
            };

            const handleReset = () => {
                clearFilters();
                setSearchValue('');
            };

            return (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space style={{ marginBottom: 8 }}>
                        <Button size="small" onClick={handleSelectAll}>
                            Select All
                        </Button>
                        <Button size="small" onClick={handleUnselectAll}>
                            Unselect All
                        </Button>
                    </Space>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                        <Checkbox.Group
                            value={selectedKeys}
                            onChange={setSelectedKeys}
                            style={{ display: 'flex', flexDirection: 'column' }}
                        >
                            {filteredOptions.map(option => (
                                <Checkbox key={option.value} value={option.value} style={{ marginBottom: 4 }}>
                                    {option.text}
                                </Checkbox>
                            ))}
                        </Checkbox.Group>
                    </div>
                    <Space style={{ marginTop: 8 }}>
                        <Button size="small" type="primary" onClick={handleConfirm}>
                            OK
                        </Button>
                        <Button size="small" onClick={handleReset}>
                            Reset
                        </Button>
                    </Space>
                </div>
            );
        };
        
        FilterDropdown.displayName = `FilterDropdown_${dataIndex}`;
        return FilterDropdown;
    };

    // Function to normalize text for better matching
    const normalizeText = (text: string): string => {
        return text
            .toLowerCase()
            .replace(/[""'']/g, '') // Remove various quote types
            .replace(/[^\w\s]/g, '') // Remove all special characters except letters, numbers, and spaces
            .replace(/\s+/g, '') // Remove all spaces
            .trim();
    };

    // Function to generate smart search terms and trigger metadata search
    const handleFindBestMatch = (record: UnmatchedAssetInterface) => {
        if (!onSearchMetadata) return;
        
        const title = record.title || '';
        const refId = record.ref_id || '';
        
        // First try exact ref_id match if available
        if (refId.trim()) {
            onSearchMetadata(refId.trim());
            return;
        }
        
        // Then try title-based search with multiple strategies
        if (title.trim()) {
            // Strategy 1: Try exact title first (just remove quotes)
            const exactTitle = title.replace(/[""'']/g, '').trim();
            onSearchMetadata(exactTitle);
            
            // Note: We'll search for normalized version on the backend
            // The frontend just sends the cleaned title and the backend will handle fuzzy matching
        }
    };

    // Make sure titleConflictData is an array before mapping
    const transformedData = Array.isArray(titleConflictData) 
        ? titleConflictData
            .filter(item => item && typeof item === 'object') // Filter out null/undefined items
            .map(item => ({
                ...item,
                key: item.id || Math.random().toString(), // Fallback key for safety
                ID: item.id || '',
                REF_ID: item.ref_id || '',
                TITLE: (item.title || '').replace(/[""]/g, ''), // Remove double quotes from title display
                NOTES: item.notes || '',
                // Status now includes "Linked" state when metadata is linked
                STATUS: (item.isProcessing || (item.id && processingRecords[item.id]) || item.processed === 'pending') 
                    ? 'Processing' 
                    : (item.id && linkedMetadata[item.id])
                        ? 'Linked'
                        : 'Ready'
            }))
        : [];

    const actionsColumn = {
        title: 'Actions',
        key: 'actions',
        width: 120, // Increased width to accommodate multiple buttons
        render: (_: any, record: UnmatchedAssetInterface) => {
            if (!record || !record.id) {
                return null; // Don't render actions for invalid records
            }
            
            // Item is processing if it has local processing state, server pending state, or isProcessing flag
            const isProcessing = !!(
                (record.id && processingRecords[record.id] === true) || 
                record.processed === 'pending' || 
                record.isProcessing
            );
            
            return (
                <div style={{ 
                    display: 'flex',
                    gap: '4px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '0'
                }}>
                    {isProcessing ? (
                        <SyncOutlined spin style={{ color: '#1890ff' }} />
                    ) : (
                        <>
                            <Button
                                type="link"
                                size="small"
                                icon={<SearchOutlined />}
                                onClick={() => handleFindBestMatch(record)}
                                disabled={isProcessing}
                                title="Find best metadata match"
                                style={{ padding: '0 4px', minWidth: 'auto' }}
                            >
                                Find Match
                            </Button>
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: '1',
                                            label: 'Create Alternate Title',
                                            onClick: () => handleOpenAlternateTitleForm(record)
                                        },
                                        {
                                            key: '2',
                                            label: 'Remove / Mark as Promo',
                                            onClick: () => handleRemoveMarkAsPromo(record)
                                        }
                                    ]
                                }}
                                trigger={['click']}
                                disabled={isProcessing}
                            >
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<SettingOutlined />}
                                    style={{ padding: '0 4px', minWidth: 'auto' }}
                                    title="More actions"
                                />
                            </Dropdown>
                        </>
                    )}
                </div>
            );
        }
    };

    // Create the columns array with modified order - status column NOT next to actions
    const platformsColumn = {
        title: 'Platforms',
        dataIndex: 'NOTES',
        key: 'NOTES',
        width: 120,
        ellipsis: true,
        sorter: true,
        sortOrder: sortConfig?.columnKey === 'NOTES' ? sortConfig?.order : undefined,
        filterDropdown: (() => {
            const platformValues = new Set<string>();
            transformedData.forEach(item => {
                const notes = item.NOTES || '';
                let platforms = '';
                if (notes.includes("Platforms:") && notes.includes("Dates:")) {
                    const startIndex = notes.indexOf("Platforms:") + "Platforms:".length;
                    const endIndex = notes.indexOf("Dates:");
                    if (startIndex < endIndex) {
                        platforms = notes.substring(startIndex, endIndex).trim();
                    }
                } else if (notes.includes("Platforms:")) {
                    const startIndex = notes.indexOf("Platforms:") + "Platforms:".length;
                    platforms = notes.substring(startIndex).split(";")[0].trim();
                } else if (notes.includes("Platform:")) {
                    const startIndex = notes.indexOf("Platform:") + "Platform:".length;
                    if (notes.includes("Dates:")) {
                        const endIndex = notes.indexOf("Dates:");
                        if (startIndex < endIndex) {
                            platforms = notes.substring(startIndex, endIndex).trim();
                        }
                    } else {
                        platforms = notes.substring(startIndex).split(";")[0].trim();
                    }
                }
                platforms = platforms.replace(/;/g, '').trim();
                if (platforms) platformValues.add(platforms);
            });
            const filterOptions = Array.from(platformValues).map(value => ({ text: value as string, value: value as string }));
            return createFilterDropdown('NOTES', filterOptions);
        })(),
        onFilter: (value: any, record: any) => {
            const notes = record.NOTES || '';
            let platforms = '';
            if (notes.includes("Platforms:") && notes.includes("Dates:")) {
                const startIndex = notes.indexOf("Platforms:") + "Platforms:".length;
                const endIndex = notes.indexOf("Dates:");
                if (startIndex < endIndex) {
                    platforms = notes.substring(startIndex, endIndex).trim();
                }
            } else if (notes.includes("Platforms:")) {
                const startIndex = notes.indexOf("Platforms:") + "Platforms:".length;
                platforms = notes.substring(startIndex).split(";")[0].trim();
            } else if (notes.includes("Platform:")) {
                const startIndex = notes.indexOf("Platform:") + "Platform:".length;
                if (notes.includes("Dates:")) {
                    const endIndex = notes.indexOf("Dates:");
                    if (startIndex < endIndex) {
                        platforms = notes.substring(startIndex, endIndex).trim();
                    }
                } else {
                    platforms = notes.substring(startIndex).split(";")[0].trim();
                }
            }
            platforms = platforms.replace(/;/g, '').trim();
            return platforms.toLowerCase().includes(value.toLowerCase());
        },
        filterMultiple: true,
        render: (notes: string) => {
            let platforms = '-';
            
            // Extract platforms from notes if present
            if (notes) {
                // Get just the text between "Platforms:" and "Dates:" if both exist
                if (notes.includes("Platforms:") && notes.includes("Dates:")) {
                    const startIndex = notes.indexOf("Platforms:") + "Platforms:".length;
                    const endIndex = notes.indexOf("Dates:");
                    if (startIndex < endIndex) {
                        platforms = notes.substring(startIndex, endIndex).trim();
                    }
                }
                // Otherwise, just get everything after "Platforms:" if it exists
                else if (notes.includes("Platforms:")) {
                    const startIndex = notes.indexOf("Platforms:") + "Platforms:".length;
                    platforms = notes.substring(startIndex).split(";")[0].trim();
                }
                // Check for "Platform:" (singular) if "Platforms:" wasn't found
                else if (notes.includes("Platform:")) {
                    const startIndex = notes.indexOf("Platform:") + "Platform:".length;
                    if (notes.includes("Dates:")) {
                        const endIndex = notes.indexOf("Dates:");
                        if (startIndex < endIndex) {
                            platforms = notes.substring(startIndex, endIndex).trim();
                        }
                    } else {
                        platforms = notes.substring(startIndex).split(";")[0].trim();
                    }
                }
                
                // Remove any semicolons that might still be present
                platforms = platforms.replace(/;/g, '').trim();
            }
            
            return <span>{platforms}</span>
        }
    };

    const linkedMetadataColumn = {
        title: 'Linked Metadata',
        dataIndex: 'LINKED_METADATA',
        key: 'LINKED_METADATA',
        width: 80,
        ellipsis: true,
    };

    const statusColumn = {
        title: 'Status',
        dataIndex: 'STATUS',
        key: 'STATUS',
        width: 100,
        sorter: true,
        sortOrder: sortConfig?.columnKey === 'STATUS' ? sortConfig?.order : undefined,
        render: (status: string) => {
            let color = 'inherit';
            let fontWeight = 'normal';
            
            if (status === 'Processing') {
                color = '#1890ff'; // Blue for processing
                fontWeight = 'bold';
            } else if (status === 'Linked') {
                color = '#52c41a'; // Green for linked
                fontWeight = 'bold';
            }
            
            return (
                <span style={{ 
                    color: color,
                    fontWeight: fontWeight
                }}>
                    {status}
                </span>
            );
        }
    };

    // Get unique values for filters (without Select All options since we handle that in custom dropdown)
    const getUniqueValues = (dataIndex: string) => {
        const uniqueValues = new Set(
            transformedData
                .map(item => item[dataIndex as keyof UnmatchedAssetInterface])
                .filter(Boolean)
                .map(value => String(value).trim())
        );
        return Array.from(uniqueValues).map(value => ({ text: value, value: value }));
    };

    // Create enhanced columns with filters and proper sorting
    const enhancedColumns = titleConflictColumns.map(column => {
        const dataIndex = column.dataIndex || column.key;
        
        if (column.dataIndex === 'TITLE' || column.key === 'TITLE') {
            const titleOptions = getUniqueValues('TITLE');
            return { 
                ...column, 
                width: 150,
                key: 'TITLE',
                sorter: true,
                sortOrder: sortConfig?.columnKey === 'TITLE' ? sortConfig?.order : undefined,
                filterDropdown: createFilterDropdown('TITLE', titleOptions),
                onFilter: (value: any, record: any) => {
                    return (record.TITLE || '').toString().toLowerCase().includes(value.toLowerCase());
                },
                filterMultiple: true
            };
        }
        if (column.dataIndex === 'REF_ID' || column.key === 'ref_id') {
            const refIdOptions = getUniqueValues('REF_ID');
            return { 
                ...column, 
                width: 120,
                key: 'REF_ID',
                sorter: true,
                sortOrder: sortConfig?.columnKey === 'REF_ID' ? sortConfig?.order : undefined,
                filterDropdown: createFilterDropdown('REF_ID', refIdOptions),
                onFilter: (value: any, record: any) => {
                    return (record.REF_ID || '').toString().toLowerCase().includes(value.toLowerCase());
                },
                filterMultiple: true
            };
        }
        return {
            ...column,
            sorter: true,
            sortOrder: sortConfig?.columnKey === column.key ? sortConfig?.order : undefined
        };
    });
    
    // Remove linkedMetadataColumn and arrange other columns with fixed widths
    const updatedColumns = [
        ...enhancedColumns,
        platformsColumn,
        statusColumn,
        actionsColumn
    ];

    const handleTableChange = (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        setCurrentPage(pagination.current || 1);
        setPageSize(pagination.pageSize || 10);
        
        // Handle sorting
        console.log('Sorter object:', sorter); // Debug log
        if (sorter && (sorter.columnKey || sorter.field || sorter.column?.key) && sorter.order) {
            const columnKey = sorter.columnKey || sorter.field || sorter.column?.key;
            console.log('Setting sort config:', { columnKey, order: sorter.order }); // Debug log
            setSortConfig({
                columnKey: columnKey,
                order: sorter.order
            });
        } else {
            console.log('Clearing sort config'); // Debug log
            setSortConfig(null);
        }
    };

    // Apply sorting to the full dataset
    const sortedData = useMemo(() => {
        console.log('Current sortConfig:', sortConfig); // Debug log
        if (!sortConfig) return transformedData;
        
        return [...transformedData].sort((a, b) => {
            const { columnKey, order } = sortConfig;
            console.log('Sorting by:', columnKey, 'order:', order); // Debug log
            
            let aVal = '';
            let bVal = '';
            
            // Handle different column types
            if (columnKey === 'TITLE') {
                aVal = a.TITLE || '';
                bVal = b.TITLE || '';
            } else if (columnKey === 'REF_ID' || columnKey === 'ref_id') {
                aVal = a.REF_ID || '';
                bVal = b.REF_ID || '';
            } else if (columnKey === 'NOTES') {
                // For platforms column, extract platform text
                const getPlatformText = (notes: string) => {
                    if (!notes) return '';
                    let platforms = '';
                    if (notes.includes("Platforms:") && notes.includes("Dates:")) {
                        const startIndex = notes.indexOf("Platforms:") + "Platforms:".length;
                        const endIndex = notes.indexOf("Dates:");
                        if (startIndex < endIndex) {
                            platforms = notes.substring(startIndex, endIndex).trim();
                        }
                    } else if (notes.includes("Platforms:")) {
                        const startIndex = notes.indexOf("Platforms:") + "Platforms:".length;
                        platforms = notes.substring(startIndex).split(";")[0].trim();
                    } else if (notes.includes("Platform:")) {
                        const startIndex = notes.indexOf("Platform:") + "Platform:".length;
                        if (notes.includes("Dates:")) {
                            const endIndex = notes.indexOf("Dates:");
                            if (startIndex < endIndex) {
                                platforms = notes.substring(startIndex, endIndex).trim();
                            }
                        } else {
                            platforms = notes.substring(startIndex).split(";")[0].trim();
                        }
                    }
                    return platforms.replace(/;/g, '').trim();
                };
                aVal = getPlatformText(a.NOTES || '');
                bVal = getPlatformText(b.NOTES || '');
            } else if (columnKey === 'STATUS') {
                aVal = a.STATUS || '';
                bVal = b.STATUS || '';
            } else if (columnKey === 'notes') {
                aVal = a.notes || '';
                bVal = b.notes || '';
            }
            
            const comparison = aVal.localeCompare(bVal);
            return order === 'ascend' ? comparison : -comparison;
        });
    }, [transformedData, sortConfig]);
    
    const paginatedData = sortedData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    return (
        <StyledDiv>
            <Row style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Col style={{ display: 'flex', alignItems: 'center' }}>
                    <ComponentTitle>Unmatched Assets</ComponentTitle>
                    <Button onClick={fetchTitleConflicts} style={{marginLeft: "8px"}} icon={<ReloadOutlined />}>Reload</Button>
                </Col>
                <Col>
                    <Button 
                        type="default"
                        icon={<PlayCircleOutlined />}
                        onClick={handleReprocessAllConflicts}
                        loading={isReprocessing}
                        disabled={isReprocessing}
                    >
                        {isReprocessing ? 'Reprocessing...' : 'Reprocess'}
                    </Button>
                </Col>
            </Row>
            <div style={{ marginBottom: '50px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Table<UnmatchedAssetInterface>
                    columns={[
                        {
                            title: '',
                            key: 'select',
                            width: 50,
                            render: (_, record) => {
                                const isSelected = selectedConflict?.id === record.id;
                                const isDisabled = !!(
                                    (record.id && processingRecords[record.id] === true) || 
                                    record.processed === 'pending' || 
                                    record.isProcessing
                                );
                                
                                return (
                                    <input 
                                        type="checkbox" 
                                        checked={isSelected}
                                        onChange={() => {
                                            if (isSelected) {
                                                // Unselect if already selected
                                                onConflictSelect({} as UnmatchedAssetInterface);
                                            } else {
                                                // Select this record
                                                onConflictSelect(record);
                                            }
                                        }}
                                        disabled={isDisabled}
                                        style={{ transform: 'scale(1.3)', cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                                    />
                                );
                            }
                        },
                        ...updatedColumns
                    ]}
                    dataSource={paginatedData}
                    scroll={{ y: 'calc(100vh - 280px)',  x: 600 }}
                    pagination={false} // We'll handle pagination separately
                    loading={isLoading}
                    rowKey={(record) => record?.id || Math.random().toString()} // Fallback key for safety
                    onChange={handleTableChange}
                    rowClassName={(record) => {
                        // Apply processing-row class if the record is being processed
                        const isProcessing = !!(processingRecords[record?.id] || record?.processed === 'pending' || record?.isProcessing);
                        const isSelected = selectedConflict?.id === record.id;
                        
                        if (isProcessing) return 'processing-row';
                        if (isSelected) return 'ant-table-row-selected';
                        return '';
                    }}
                    locale={{
                        emptyText: isLoading ? 'Loading...' : 'No unmatched assets found. All assets are matched!'
                    }}
                    components={{
                        body: {
                          row: (props: any) => {
                            const record: any = props["data-row-key"]
                              ? paginatedData.find((d) => d.key === props["data-row-key"])
                              : null;
                    
                            return record ? (
                              <Tooltip title={record.notes}>
                                <tr {...props} />
                              </Tooltip>
                            ) : (
                              <tr {...props} />
                            );
                          },
                        },
                      }}
                    
                />
            </div>
            
            {/* Fixed position pagination */}
            <div style={{ 
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                paddingTop: '12px',
                paddingBottom: '8px',
                borderTop: '1px solid #d9d9d9',
                display: 'flex',
                justifyContent: 'flex-end',
                backgroundColor: '#f0f2f5',
                paddingLeft: '16px',
                paddingRight: '16px',
                borderBottomLeftRadius: '4px',
                borderBottomRightRadius: '4px',
                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)',
                zIndex: 10
            }}>
                <Pagination
                    showSizeChanger
                    size="small"
                    current={currentPage}
                    pageSize={pageSize}
                    total={sortedData.length}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        if (size) setPageSize(size);
                    }}
                    pageSizeOptions={['6', '10', '20']}
                    showTotal={(total) => `Total: ${total}`}
                />
            </div>
            
            <AlternateTitleForm
                isOpen={alternateTitles.isFormOpen}
                saveEditData={handleSaveAlternateTitle}
                form={alternateTitles.form}
                isLoading={alternateTitles.isLoading}
                handleCancel={alternateTitles.handleCancel}
                editId={alternateTitles.editId}
            />
        </StyledDiv>
    );
};

export default UnmatchedAssets;